import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../../../organisms/Form/utils";

import { DatePicker, DatePickerProps } from "../../../../../molecules/DatePicker";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from DatePicker that aren't in BaseFormFieldProps
type UniqueInputProps = Omit<DatePickerProps, keyof BaseFormFieldProps<Date | null> | "value" | "onChange">;

export type FormDatePickerProps = Omit<BaseFormFieldProps<Date | null>, "input"> & UniqueInputProps;

/**
 * This is a form component that is used to create a date picker field.
 * It is a wrapper around the DatePicker component.
 * It is used to create a date picker field that is connected to the PyForm context.
 * Its props cross-cut with the DatePicker component and the FormField component.
 *
 * DatePicker is a component that allows users to select a single date.
 * It is a controlled component that uses the value and onChange props to update the date value.
 */
export const FormDatePicker = (props: FormDatePickerProps) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	if (!form) {
		throw new Error("FormDatePicker must be used within a Form component");
	}

	return (
		<FormField<Date | null>
			{...inputWrapperProps}
			input={(field) => {
				return (
					<DatePicker
						{...inputProps}
						allowEmpty={true}
						value={field.value}
						onChange={field.onChange}
						id={inputWrapperProps.id}
						data-testid={`${inputWrapperProps["data-testid"]}-date-picker`}
						disabled={inputWrapperProps.disabled}
					/>
				);
			}}
		/>
	);
};

FormDatePicker.displayName = "FormDatePicker";

export default FormDatePicker;
